/**
 * Data classes — ADR-0073 §2 (V105-2-3), Settings → Security #data-classes.
 *
 * GET  /settings/field-classes  any member (field keys + classes; no customer data).
 * POST /settings/field-classes  owner / admin: raise one field (or every tenant field)
 *      to federal with a reason. Raise-only: nothing here lowers a class.
 *
 * Only fields that hold customer data are listed; public reference content
 * (register text, rule text) has nothing to raise.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Card, Field, Input, Spinner } from '@aigovops/design-system';
import { getFieldClasses, raiseFieldClass, type FieldClassRow, type PromptDataClass } from '../lib/api';
import { queryKeys } from '../lib/query-keys';
import { errorText } from './today/shared';
import { AskAdmin } from './AskAdmin';
import './data-protection-settings.css';

const CLASS_LABEL: Record<PromptDataClass, string> = { reference: 'Public', tenant: 'Your data', federal: 'Federal' };

export function FieldClassSettings({ slug, role }: { slug: string; role: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: queryKeys.fieldClasses(slug), queryFn: () => getFieldClasses(slug), retry: false, staleTime: 60_000 });
  const canRaise = role === 'owner' || role === 'admin';
  const [open, setOpen] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState('');

  const rows = (q.data?.fields ?? []).filter((f) => f.field_key === '*' || f.default_class !== 'reference');
  const allFederal = rows.find((f) => f.field_key === '*')?.effective_class === 'federal';

  const openFor = (f: FieldClassRow) => { setOpen(f.field_key); setReason(''); setErr(null); window.setTimeout(() => document.getElementById(`dps-reason-${cssId(f.field_key)}`)?.focus(), 0); };
  const submit = async (f: FieldClassRow) => {
    if (busy) return;
    if (reason.trim().length < 10) { setErr('Say why (at least 10 characters).'); return; }
    setBusy(true); setErr(null);
    try {
      const r = await raiseFieldClass(slug, { field_key: f.field_key, class: 'federal', reason: reason.trim() });
      qc.setQueryData(queryKeys.fieldClasses(slug), { fields: r.fields });
      void qc.invalidateQueries({ queryKey: ['federal-parity', slug] });
      setOpen(null);
      setMsg(`${f.label}: federal`);
    } catch (e) { setErr(errorText(e, 'That did not save.')); }
    finally { setBusy(false); }
  };

  return (
    <div id="data-classes" className="dps" tabIndex={-1} role="region" aria-labelledby="dps-classes-h">
      <Card>
        <div className="dps-body">
          <h2 id="dps-classes-h" className="dps-h">Data classes</h2>
          {q.isLoading ? <Spinner label="Loading data classes" /> : q.error || !q.data ? (
            <Alert variant="error"><p>Data classes could not load.</p><Button compact onClick={() => void q.refetch()}>Retry</Button></Alert>
          ) : (
            <>
              <ul className="dps-list" aria-label="Data classes">
                {rows.map((f) => {
                  const covered = f.field_key !== '*' && allFederal;
                  const raisable = f.raisable_to.includes('federal') && !covered;
                  const id = cssId(f.field_key);
                  return (
                    <li key={f.field_key} className="dps-item">
                      <span>{f.label}</span>
                      <Badge tone={f.effective_class === 'federal' || covered ? 'accent' : 'neutral'}>{CLASS_LABEL[covered ? 'federal' : f.effective_class]}</Badge>
                      {canRaise && raisable && open !== f.field_key
                        ? <Button variant="ghost" compact aria-label={`Raise ${f.label} to federal`} onClick={() => openFor(f)}>Raise to federal</Button>
                        : <span aria-hidden="true" />}
                      {open === f.field_key ? (
                        <div className="dps-item-form dps-confirm">
                          <Field id={`dps-reason-${id}`} label="Why">
                            <Input value={reason} maxLength={500} aria-invalid={err ? true : undefined} aria-describedby={err ? `dps-err-${id}` : undefined}
                              onChange={(e) => { setReason(e.currentTarget.value); setErr(null); }}
                              onKeyDown={(e) => { if (e.key === 'Enter') void submit(f); }} />
                          </Field>
                          {err ? <p id={`dps-err-${id}`} className="dps-err" role="alert">{err}</p> : null}
                          <div className="dps-row">
                            <Button variant="secondary" compact aria-disabled={busy || undefined} onClick={() => void submit(f)}>{busy ? 'Saving…' : "Raise — can't be lowered"}</Button>
                            <Button variant="ghost" compact onClick={() => { setOpen(null); setErr(null); }}>Cancel</Button>
                          </div>
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
              {!canRaise ? <AskAdmin slug={slug} what="raises a data class" subject="Raise a data class to federal" /> : null}
            </>
          )}
          <p role="status" className="sr-only">{msg}</p>
        </div>
      </Card>
    </div>
  );
}

function cssId(key: string): string {
  return key === '*' ? 'all' : key.replace(/[^a-z0-9]+/gi, '-');
}
